<template>
  <div
    class="current-state-panel player-debug-overlay-glass player-debug-overlay-legible z-50 flex min-w-[200px] max-w-[min(240px,calc(100vw-2rem))] flex-col overflow-hidden rounded-lg border border-white/15 text-white/90 shadow-2xl shadow-black/70 ring-1 ring-black/30 backdrop-blur-md xl:min-w-[260px] xl:max-w-[min(280px,calc(100vw-2rem))]"
    :class="containerClasses"
  >
    <!-- Header (aligned with Feed / Group sync notification chrome) -->
    <div class="border-b border-white/15 bg-white/[0.03] px-3 py-2">
      <div class="flex items-center justify-between gap-2">
        <span class="text-[11px] font-semibold tracking-wide text-white">Lucimark Player</span>
        <div class="flex items-center gap-1">
          <span
            v-if="showNativePlaybackSurfaceChip"
            class="inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-medium"
            :class="nativePlaybackSurfaceChip.enabled
              ? 'border-green-500/35 bg-green-500/15 text-green-300'
              : 'border-white/20 bg-white/[0.06] text-white/55'"
            :title="nativePlaybackSurfaceChip.ariaLabel"
            :aria-label="nativePlaybackSurfaceChip.ariaLabel"
          >
            <svg
              class="size-3.5 shrink-0"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M17.6 9.48l1.84-3.18c.16-.31.04-.69-.26-.85a.64.64 0 0 0-.83.22l-1.88 3.24a11.43 11.43 0 0 0-8.94 0L5.65 5.67a.64.64 0 0 0-.83-.22c-.3.16-.42.54-.26.85l1.84 3.18C4.57 11.03 3.34 13.32 3.34 16v.17c0 .48.39.86.86.86h15.6c.47 0 .86-.38.86-.86V16c0-2.68-1.23-4.97-3.06-6.52zM7.5 14.25a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5zm9 0a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5z" />
            </svg>
            <span v-if="nativePlaybackSurfaceChip.detail">{{ nativePlaybackSurfaceChip.detail }}</span>
          </span>
          <div
            class="flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-medium"
            :class="networkChipToneClass"
          >
            <Wifi class="size-3.5 shrink-0" :stroke-width="2.25" />
            <span>{{ networkChip.label }}</span>
          </div>
        </div>
      </div>
      <div class="mt-1 flex flex-col gap-0.5 text-[10px] font-medium text-white/70">
        <!-- App v = native shell only; plain web shows Web # alone -->
        <span v-if="displayAppVersion">App v{{ displayAppVersion }}</span>
        <span v-if="networkChip.detail" class="text-white/55">{{ networkChip.detail }}</span>
        <span :class="webBundleHighlightClass">Web #{{ webBundleDisplay }}</span>
        <span
          v-if="localAutoHideCountdown"
          class="text-amber-200/90"
          data-testid="status-overlay-auto-hide"
        >
          Fecha em {{ localAutoHideCountdown }}
        </span>
      </div>
    </div>

    <!-- Body -->
    <div class="flex min-w-0 flex-col gap-1.5 px-3 py-2 text-[10px] leading-tight">
      <!-- Badges row -->
      <div class="flex flex-wrap gap-1">
        <span
          v-if="update"
          class="flex items-center gap-1 rounded-full border border-blue-400/35 bg-blue-500/15 px-2 py-px text-[9px] font-medium text-blue-200"
        >
          <ArrowUpCircle class="size-3 shrink-0" :stroke-width="2.25" />
          v{{ update.version }} · {{ formatApkUpdateBadge(update.status) }}{{ update.download != null && typeof update.download === 'number' ? ` ${Math.round(update.download)}%` : '' }}
        </span>
        <span
          v-if="widgetsLoadingCount > 0"
          class="rounded-full border border-amber-400/35 bg-amber-500/15 px-2 py-px text-[9px] font-medium text-amber-200"
        >
          {{ widgetsLoadingCount }} widget(s) loading…
        </span>
        <span
          v-if="audienceAnalyticsBadge"
          class="flex items-center gap-1 rounded-full border px-2 py-px text-[9px] font-medium"
          :class="audienceAnalyticsBadge.chipClass"
        >
          <component
            :is="audienceAnalyticsBadge.icon"
            class="size-3 shrink-0"
            :stroke-width="2.25"
            :class="audienceAnalyticsBadge.iconClass"
          />
          {{ audienceAnalyticsBadge.label }}
        </span>
        <span
          v-if="weeklyAvailabilityBadge"
          class="flex items-center gap-1 rounded-full border px-2 py-px text-[9px] font-medium"
          :class="weeklyAvailabilityBadge.chipClass"
        >
          <component
            :is="weeklyAvailabilityBadge.icon"
            class="size-3 shrink-0"
            :stroke-width="2.25"
            :class="weeklyAvailabilityBadge.iconClass"
          />
          {{ weeklyAvailabilityBadge.label }}
        </span>
      </div>

      <!-- System info -->
      <div class="flex flex-col gap-0.5">
        <div class="info-row">
          <span class="info-label">Hora</span>
          <span>{{ currentTime }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Latência</span>
          <span>{{ roundTripTime ?? 'n/a' }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Rotação</span>
          <span>{{ screenRotation }}° · {{ viewportRotationMode }}</span>
        </div>
        <div v-if="capacityTier != null" class="info-row">
          <span class="info-label">Capacidade</span>
          <span>{{ capacityTier }}{{ devCapacityTierOverrideHint }}</span>
        </div>
        <div class="info-row items-start">
          <span class="info-label flex items-center gap-0.5 shrink-0">
            <MapPin class="size-3" /> GPS
          </span>
          <span class="transition-colors" :class="geolocationRunning ? 'text-green-300' : 'text-amber-200'">
            {{ gpsStatusLine }}
          </span>
          <span class="ml-auto text-white/30">#{{ geolocationPositionUpdates }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">ESN</span>
          <span class="text-white font-medium">{{ esnDisplay }}</span>
        </div>
        <template v-if="showAudienceAnalyticsBlock">
          <div class="mt-1 border-t border-white/10 pt-1">
            <div class="mb-1 flex items-center gap-1.5 text-[9px] font-semibold uppercase tracking-widest text-white/50">
              <span class="flex items-center gap-1">
                <Video class="size-3 text-white/40" :stroke-width="2.25" />
                Público (janela atual)
              </span>
              <span
                v-if="audienceAnalyticsCollectingPulse"
                class="inline-flex size-2 shrink-0 rounded-full bg-blue-400 shadow-[0_0_8px_rgba(96,165,250,0.55)] animate-pulse"
                title="A recolher amostras"
              />
            </div>
            <div v-if="!audienceAnalyticsEnabled" class="rounded-md border border-white/10 bg-white/[0.04] px-2 py-1.5 text-white/50">
              Recolha <span class="font-medium text-white/70">desligada</span> para este player (servidor / painel).
            </div>
            <template v-else-if="audienceAnalyticsLive">
              <div class="info-row">
                <span class="info-label">Estado</span>
                <span :class="audienceAnalyticsLive.capturing ? 'text-blue-300' : 'text-amber-200'">
                  {{
                    audienceAnalyticsLive.capturing
                      ? (audienceAnalyticsLive.diffSamples > 0 ? 'a recolher' : 'câmara · 1ª amostra')
                      : 'sem captura'
                  }}
                </span>
              </div>
              <div class="info-row w-full min-w-0">
                <span class="info-label">Amostras</span>
                <span class="min-w-0 tabular-nums text-white/90">
                  {{ audienceAnalyticsLive.diffSamples }}
                  <span class="text-[9px] font-normal text-white/35"> · ~2.5s/diff</span>
                </span>
              </div>
              <div class="audience-meter">
                <div class="audience-meter__head">
                  <span>Motion</span>
                  <span class="tabular-nums text-white/55">
                    {{ audienceAnalyticsLive.motionScore != null ? audienceAnalyticsLive.motionScore.toFixed(2) : '—' }}
                  </span>
                </div>
                <div class="audience-meter__track">
                  <div
                    class="audience-meter__fill audience-meter__fill--motion"
                    :style="{ width: `${audienceMotionBarPct}%` }"
                  />
                </div>
              </div>
              <div class="audience-meter">
                <div class="audience-meter__head">
                  <span>Presença</span>
                  <span class="tabular-nums text-white/55">
                    {{ audienceAnalyticsLive.presenceScore != null ? audienceAnalyticsLive.presenceScore.toFixed(2) : '—' }}
                  </span>
                </div>
                <div class="audience-meter__track">
                  <div
                    class="audience-meter__fill audience-meter__fill--presence"
                    :style="{ width: `${audiencePresenceBarPct}%` }"
                  />
                </div>
              </div>
            </template>
            <div
              v-else
              class="space-y-1 rounded-md border border-amber-500/20 bg-amber-500/[0.07] px-2 py-1.5 text-amber-100/90"
            >
              <p class="text-[10px] font-medium text-amber-200/95">
                Pipeline inativo
              </p>
              <p class="text-[9px] leading-snug text-amber-100/75">
                {{ audienceAnalyticsInactiveDetail }}
              </p>
            </div>
            <div
              v-if="audienceAnalyticsEnabled"
              class="mt-1.5 border-t border-dashed border-white/15 pt-1.5"
            >
              <div class="mb-1 text-[9px] font-semibold uppercase tracking-widest text-white/45">
                Agregados (heartbeat → API)
              </div>
              <div
                v-if="audienceAnalyticsPendingHeartbeat"
                class="rounded-md border border-cyan-500/30 bg-cyan-500/[0.08] px-2 py-1.5"
              >
                <div class="text-[9px] font-medium text-cyan-200/95">
                  Na fila · próximo POST
                </div>
                <div class="mt-0.5 font-mono text-[10px] leading-relaxed text-white/90">
                  motion {{ formatAudienceAggScore(audienceAnalyticsPendingHeartbeat.motionScore) }}
                  · presença {{ formatAudienceAggScore(audienceAnalyticsPendingHeartbeat.presenceScore) }}
                  · ativo {{ formatAudienceAggActiveDwell(audienceAnalyticsPendingHeartbeat) }}
                  · sessões {{ formatAudienceAggMotionSessions(audienceAnalyticsPendingHeartbeat) }}
                  · janela {{ audienceAnalyticsPendingHeartbeat.intervalSeconds }}s
                  <span class="text-white/55">{{ formatAudienceCvPersonSuffix(audienceAnalyticsPendingHeartbeat) }}</span>
                </div>
              </div>
              <div v-else class="rounded-md border border-white/[0.06] bg-white/[0.03] px-2 py-1 text-[9px] text-white/40">
                Fila vazia — ainda não há intervalo fechado à espera de envio.
              </div>
              <div
                v-if="audienceAnalyticsLastSentAggregate"
                class="mt-1 rounded-md border border-white/12 bg-white/[0.05] px-2 py-1.5"
              >
                <div class="text-[9px] font-medium text-white/55">
                  Último incluído num heartbeat
                </div>
                <div class="mt-0.5 font-mono text-[10px] leading-relaxed text-white/85">
                  motion {{ formatAudienceAggScore(audienceAnalyticsLastSentAggregate.motionScore) }}
                  · presença {{ formatAudienceAggScore(audienceAnalyticsLastSentAggregate.presenceScore) }}
                  · ativo {{ formatAudienceAggActiveDwell(audienceAnalyticsLastSentAggregate) }}
                  · sessões {{ formatAudienceAggMotionSessions(audienceAnalyticsLastSentAggregate) }}
                  · janela {{ audienceAnalyticsLastSentAggregate.intervalSeconds }}s
                  <span class="text-white/55">{{ formatAudienceCvPersonSuffix(audienceAnalyticsLastSentAggregate) }}</span>
                </div>
              </div>
              <p v-else class="mt-1 text-[9px] text-white/35">
                Último POST: ainda nenhum agregado nesta sessão.
              </p>
              <p
                v-if="audiencePersonCvDebugHint"
                class="mt-1.5 rounded-md border border-violet-500/25 bg-violet-500/[0.06] px-2 py-1 text-[9px] leading-snug text-violet-100/85"
              >
                {{ audiencePersonCvDebugHint }}
              </p>
            </div>
          </div>
        </template>
      </div>

      <!-- Tenant section -->
      <transition
        enter-from-class="max-h-0 opacity-0"
        enter-to-class="max-h-[300px] opacity-100"
        enter-active-class="transition-all duration-[2s]"
        leave-from-class="max-h-[300px] opacity-100"
        leave-to-class="max-h-0 opacity-0"
        leave-active-class="transition-all duration-1000"
      >
        <div v-if="tenant !== null" class="flex flex-col gap-0.5 border-t border-white/15 pt-1.5">
          <div class="org-heading mb-0.5 text-[9px] font-semibold uppercase tracking-widest text-white/50">
            Organização
          </div>
          <div class="info-row">
            <span class="info-label">Nome</span>
            <span>{{ playerTitle || playerGroup?.name || '—' }}</span>
          </div>
          <div
            v-if="distributionListsSummary != null"
            class="info-row items-start"
            data-testid="current-state-distribution-lists"
          >
            <span class="info-label shrink-0">Listas</span>
            <span class="min-w-0 flex-1 break-words">{{ distributionListsSummary }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Org</span>
            <span>{{ tenant?.name ?? '—' }}</span>
          </div>
          <div
            v-if="contentOrFeedSummary != null"
            class="info-row items-start"
            data-testid="current-state-content-or-feed"
          >
            <span class="info-label shrink-0 inline-flex items-center gap-1">
              {{ contentOrFeedSummary.label }}
              <Loader2
                v-if="contentChangeBusy"
                class="size-2.5 shrink-0 animate-spin text-cyan-200/85"
                :stroke-width="2.5"
                data-testid="current-state-content-change-spinner"
                aria-hidden="true"
              />
            </span>
            <span class="min-w-0 flex-1 break-words">{{ contentOrFeedSummary.value }}</span>
          </div>
          <div
            v-if="idleQueueFeedbackLine != null"
            class="info-row items-start"
            data-testid="current-state-idle-queue-feedback"
          >
            <span class="info-label shrink-0">Fila</span>
            <span
              class="min-w-0 flex-1 break-words"
              :class="idleQueueFeedbackLine.tone === 'warning' || idleQueueFeedbackLine.tone === 'error'
                ? 'text-amber-200/90'
                : 'text-white/70'"
            >
              {{ idleQueueFeedbackLine.headline }}
            </span>
          </div>
          <div
            v-if="cachedConfigurationLine != null"
            class="info-row items-start"
            data-testid="current-state-cached-config"
          >
            <span class="info-label shrink-0">Config</span>
            <span class="min-w-0 flex-1 break-words text-amber-200/90">{{ cachedConfigurationLine }}</span>
          </div>
          <div v-if="nextCampaignAsLegacy" class="info-row items-start">
            <span class="info-label shrink-0">Próx. feed</span>
            <span>
              {{ nextCampaignAsLegacy.title }}
              <span class="text-white/40">v{{ nextCampaignAsLegacy.version }}</span>
              <span v-if="nextCampaignAsLegacy.startDate" class="text-white/40"> ({{ formatNextCampaignStart }})</span>
            </span>
          </div>
          <div
            v-if="weeklyAvailabilityStatus != null"
            class="mt-1 border-t border-white/10 pt-1"
          >
            <div class="org-heading mb-0.5 text-[9px] font-semibold uppercase tracking-widest text-white/50">
              Disponibilidade semanal
            </div>
            <div class="info-row items-start">
              <span class="info-label shrink-0">Estado</span>
              <span
                class="min-w-0 flex-1 break-words"
                :class="weeklyAvailabilityStatus.suppressed ? 'text-violet-300' : 'text-emerald-300'"
              >
                {{ weeklyAvailabilityStatus.statusLabel }}
              </span>
            </div>
            <div
              v-if="weeklyAvailabilityStatus.nextTransitionHint"
              class="info-row items-start"
            >
              <span class="info-label shrink-0">Próximo</span>
              <span>{{ weeklyAvailabilityStatus.nextTransitionHint }}</span>
            </div>
            <div
              v-for="(line, wi) in weeklyAvailabilityStatus.lines"
              :key="wi"
              class="mt-0.5 break-words text-[9px] leading-snug text-white/55"
            >
              {{ line }}
            </div>
            <p
              v-if="weeklyAvailabilityStatus.suppressed"
              class="mt-1 break-words rounded-md border border-violet-500/25 bg-violet-500/[0.08] px-2 py-1 text-[9px] leading-snug whitespace-normal text-violet-100/85"
            >
              Fora das janelas configuradas o ecrã fica preto e a agenda não reproduz. As sobreposições de estado e temporizadores mantêm-se se estiverem ativas.
            </p>
          </div>
        </div>
      </transition>
    </div>

    <!-- Buttons footer -->
    <div class="btn-group border-t border-white/15 bg-white/[0.03] px-2.5 py-1.5">
      <template v-for="(btn, i) in buttons" :key="i">
        <div
          v-if="btn.if ?? true"
          class="btn-with-tip"
        >
          <button
            class="btn"
            :data-active="idx === i"
            type="button"
            :aria-label="btn.tooltip"
            @click="btn.fn()"
          >
            <component :is="btn.icon" class="size-4" :class="[btn.iconClass]" />
          </button>
          <span class="cs-tooltip" role="tooltip">{{ btn.tooltip }}</span>
        </div>
      </template>
    </div>

    <!--
      Service PIN keypad. Teleported because the panel uses backdrop-blur, which would trap a fixed
      overlay inside it. On-screen keypad instead of a text input: kiosk screens have no keyboard and
      may have no digit keys on the remote.
    -->
    <Teleport to="body">
      <div
        v-if="kioskPromptOpen"
        ref="kioskOverlayEl"
        class="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 p-4 outline-none"
        tabindex="-1"
        @dblclick.stop.prevent
        @click.stop
      >
        <div class="w-[260px] rounded-xl border border-white/15 bg-neutral-900 p-4 text-white shadow-2xl">
          <p class="text-[13px] font-semibold">
            {{ kioskPromptTitle || 'Sair do modo kiosk' }}
          </p>
          <p class="mt-1 text-[10px] leading-snug text-white/60">
            Digite o PIN de manutenção da organização.
          </p>
          <div class="my-3 flex h-6 items-center justify-center gap-2">
            <span
              v-for="n in 6"
              :key="n"
              class="size-2 rounded-full"
              :class="n <= kioskPinEntry.length ? 'bg-white' : 'bg-white/20'"
            />
          </div>
          <p v-if="kioskInCooldown" class="mb-2 text-center text-[10px] text-red-300">
            Muitas tentativas · aguarde {{ kioskCooldownRemainingSec }}s
          </p>
          <p v-else-if="kioskWrongPin" class="mb-2 text-center text-[10px] text-red-300">
            PIN incorreto · {{ kioskAttemptsLeft }} tentativa(s)
          </p>
          <div class="grid grid-cols-3 gap-1.5">
            <button
              v-for="(cell, cellIdx) in KIOSK_KEYPAD_CELLS"
              :key="cellIdx"
              class="rounded-md border py-2.5 text-sm font-semibold transition-colors active:bg-white/20"
              type="button"
              :data-kiosk-keypad-idx="cellIdx"
              :class="[
                kioskKeypadCellClass(cell),
                kioskFocusIdx === cellIdx ? 'ring-2 ring-white/80' : '',
                kioskKeypadCellDisabled(cell) ? 'opacity-40' : '',
              ]"
              :aria-disabled="kioskKeypadCellDisabled(cell) ? 'true' : undefined"
              @pointerdown="kioskFocusKeypadCell(cellIdx)"
              @click="onKioskKeypadCellClick(cellIdx, cell)"
            >
              {{ kioskKeypadCellLabel(cell) }}
            </button>
          </div>
          <button
            class="mt-2 w-full rounded-md border border-white/10 py-2 text-[10px] font-medium text-white/60 active:bg-white/10"
            type="button"
            @click="kioskCancelPrompt()"
          >
            Cancelar
          </button>
        </div>
      </div>
    </Teleport>
    <WifiSetupSheet />
  </div>
</template>

<script lang="ts" setup>
import type { IAudienceHeartbeatAggregate } from '@lucimark/shared/models/playback-heartbeat'
import type { Component } from 'vue'
import type { TKioskKeypadCell } from '@/composables/useKioskServiceUnlock'
import { Temporal } from '@js-temporal/polyfill'
import { aspectRatioFromViewportDimensions } from '@lucimark/shared/helpers/aspectRatioFromPlaylistFrameLayouts'
import { isScheduleRootAssignment } from '@lucimark/shared/helpers/playbackScheduleMaterializationIdentity'
import { overlayInstalledPlayerAppVersion } from '@lucimark/shared/helpers/playerAppVersion'
import { formatPlayerEsnOverlayDisplay } from '@lucimark/shared/helpers/playerEsnDrift'
import { effectiveViewportDimensionsForAspect } from '@lucimark/shared/helpers/playerScreenAspectRatio'
import { ArrowUpCircle, CalendarClock, HardDriveDownload, Loader2, MapPin, RotateCcw, RotateCw, Settings, Video, VideoOff, Wifi } from 'lucide-vue-next'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, toRefs, unref, watch } from 'vue'
import WifiSetupSheet from '@/components/WifiSetupSheet.vue'
import {
  isKioskServicePromptOpen,
  KIOSK_KEYPAD_CELL_ATTR,
  KIOSK_KEYPAD_CELLS,

  useKioskServiceUnlock
} from '@/composables/useKioskServiceUnlock'
import usePlayer from '@/composables/usePlayer'
import { closeWifiSetupSheet, isWifiSetupSheetOpen, useWifiProvisioning } from '@/composables/useWifiProvisioning'
import { androidRemoteKey } from '@/helpers/androidRemoteKey'
import { cssEffectiveScreenRotationFromState } from '@/helpers/cssEffectiveScreenRotation'
import { getDevHardwareCapacityTierOverride } from '@/helpers/devHardwareCapacityTierOverride'
import { formatCampaignStartDate } from '@/helpers/formatCampaignStartDate'
import { formatLocalStatusOverlayAutoHideRemaining } from '@/helpers/formatLocalStatusOverlayAutoHideRemaining'
import { formatRegistrationSnapshotAge } from '@/helpers/formatRegistrationSnapshotAge'
import { getCornerPositionClasses } from '@/helpers/getCornerPositionClasses'
import { getLocalAsUtcInstant } from '@/helpers/getLocalAsUtcInstant'
import { getEmbeddedWebBundleVersion, getWebBundleVersion } from '@/helpers/getWebBundleVersion'
import { getLocalStatusOverlayAutoHideUntilMs } from '@/helpers/localStatusOverlayAutoHide'
import { nativePlaybackSurfaceOverlayChip } from '@/helpers/nativePlaybackSurface'
import { normalizeWebOtaVersionToken } from '@/helpers/normalizeWebOtaVersionToken'
import {
  formatDistributionListsSummary,
  formatEditorialContentSummary,
  formatFeedScheduleSummary
} from '@/helpers/organizationOverlaySummary'
import { resolvePlaybackGeoPointForSchedule } from '@/helpers/playbackAssignmentGeo'
import {
  buildPlayerWeeklyAvailabilityStatusDisplay
} from '@/helpers/playerWeeklyAvailabilityStatusDisplay'
import { isRegistrationScheduleAheadOfOnAir } from '@/helpers/registrationAheadOfPlayback'
import { resolveContentChangeStatusOverlayLine } from '@/helpers/resolveContentChangeStatusOverlayLine'
import { getNativeShellAppVersionName, isTauriRuntime } from '@/helpers/resolvedPlayerAppVersion'
import { resolveGpsStatusOverlayLine } from '@/helpers/resolveGpsStatusOverlayLine'
import { resolvePlayerIdleWaitingFeedback } from '@/helpers/resolvePlayerIdleWaitingFeedback'
import { playerNetworkChipToneClass, resolvePlayerNetworkChip } from '@/helpers/resolvePlayerNetworkChip'
import { wifiText } from '@/helpers/wifiCopy'
import { getPlatform, getPlatformInstance } from '@/platform'
import { WebSocketClientState } from '@/services/WebSocketClient'

const { state, rotateScreenLeft, rotateScreenRight, scanCachedBinaryAssetsAndReloadPlayback } = usePlayer()

const { playerId, androidIdPrefix, platform, tenant, playerGroup, playerTitle, campaign, nextCampaign, geolocationRunning, geolocationError, geolocationPosition, geolocationPositionDMS, geolocationPositionUpdates, screenRotation, viewportRotationMode, screenSize, currentStateCorner, rtt, update, poolingServiceRunning, webSocketServiceState, networkStatus, widgetsLoadingCount, offeredWebOtaVersion, version: stateAppVersion, audienceAnalyticsLive, audienceAnalyticsEnabled, audienceAnalyticsPendingHeartbeat, audienceAnalyticsLastSentAggregate, lowMemoryMode, capacityTier, availabilityWeeklySchedule, weeklyAvailabilityGateEpoch, registrationConfigurationSource, registrationSnapshotSavedAtMs, queue, campaignLoadError, assignedMasterPlaylistUrn, registrationScheduleRoots, pendingPlaylistSwap, registrationStatusRefreshPending } = toRefs(state)

const networkChip = computed(() => resolvePlayerNetworkChip({
  navigatorOnline: typeof navigator !== 'undefined' && navigator.onLine,
  wifiEnabled: unref(networkStatus).wifiEnabled ?? null,
  transportConnected: unref(networkStatus).transportConnected ?? null,
  wsConnected: unref(webSocketServiceState) === WebSocketClientState.CONNECTED,
  poolingServiceRunning: unref(poolingServiceRunning)
}))

const networkChipToneClass = computed(() => playerNetworkChipToneClass(networkChip.value.tone))

const esnDisplay = computed(() => formatPlayerEsnOverlayDisplay({
  playerId: playerId.value,
  platform: platform.value,
  androidIdPrefix: androidIdPrefix.value
}))

const gpsStatusLine = computed(() => resolveGpsStatusOverlayLine({
  running: geolocationRunning.value,
  errorMessage: geolocationError.value?.message ?? null,
  positionDms: geolocationPositionDMS.value,
  positionTimestampMs: geolocationPosition.value?.timestamp ?? null
}))

const devCapacityTierOverrideHint = computed(() => {
  const override = getDevHardwareCapacityTierOverride()
  return override != null ? ` (dev → ${override})` : ''
})

/** Narrow registration root when API still sends legacy campaign fields (title/version/dates). */
const campaignAsLegacy = computed(() => {
  const c = campaign.value
  return isScheduleRootAssignment(c) ? c : null
})

const distributionListsSummary = computed((): string | null =>
  formatDistributionListsSummary(playerGroup.value?.distributionLists)
)

/**
 * With distribution lists: “Conteúdo” = editorial playlists (excludes player-master shells).
 * Without lists: “Feed” = legacy campaign/playlist summary.
 */
const contentOrFeedSummary = computed((): { label: string, value: string } | null => {
  const c = campaign.value
  const hasLists = (playerGroup.value?.distributionLists?.length ?? 0) > 0
  if (hasLists) {
    return {
      label: 'Conteúdo',
      value: formatEditorialContentSummary(c?.playlists) ?? '—'
    }
  }
  if (c == null) {
    return { label: 'Feed', value: '—' }
  }
  const legacy = campaignAsLegacy.value
  return {
    label: 'Feed',
    value: formatFeedScheduleSummary({
      playlists: c.playlists,
      rootTitle: legacy?.title ?? ('title' in c && typeof c.title === 'string' ? c.title : null),
      rootVersion: legacy?.version ?? ('version' in c && typeof c.version === 'number' ? c.version : null)
    })
  }
})

const nextCampaignAsLegacy = computed(() => {
  const c = nextCampaign.value
  return isScheduleRootAssignment(c) ? c : null
})

function formatAudienceAggScore(value: number | undefined): string {
  return value != null && Number.isFinite(value) ? value.toFixed(3) : '—'
}

function formatAudienceAggActiveDwell(agg: IAudienceHeartbeatAggregate | null | undefined): string {
  const v = agg?.estimatedActiveDwellSeconds
  return v != null && Number.isFinite(v) ? `${v.toFixed(1)}s` : '—'
}

function formatAudienceAggMotionSessions(agg: IAudienceHeartbeatAggregate | null | undefined): string {
  const v = agg?.motionBasedSessionCount
  return v != null && Number.isFinite(v) ? String(Math.trunc(v)) : '—'
}

/** Person-CV: qualified tracks + sum track-dwell — not unique visitors (see shared semantics doc). */
function formatAudienceCvPersonSuffix(agg: IAudienceHeartbeatAggregate | null | undefined): string {
  const n = agg?.personQualifiedTrackCount
  const d = agg?.personDwellSeconds
  if ((n == null || !Number.isFinite(n)) && (d == null || !Number.isFinite(d))) {
    return ''
  }
  const parts: string[] = []
  if (n != null && Number.isFinite(n)) {
    parts.push(`pistas qualif. ${Math.trunc(n)}`)
  }
  if (d != null && Number.isFinite(d)) {
    parts.push(`Σ perman. pistas ${d.toFixed(1)}s`)
  }
  return parts.length ? ` · CV ${parts.join(' · ')} (pistas ≠ visitantes únicos; soma sem dedup.)` : ''
}

const showAudienceAnalyticsBlock = computed(() => {
  const p = getPlatform()
  if (p !== 'web' && p !== 'android') {
    return false
  }
  return unref(tenant)?.audienceAnalytics === true
})

interface TAudienceChip {
  label: string
  chipClass: string
  icon: Component
  iconClass: string
}

const audienceAnalyticsBadge = computed((): TAudienceChip | null => {
  if (!showAudienceAnalyticsBlock.value) {
    return null
  }
  if (!unref(audienceAnalyticsEnabled)) {
    return {
      label: 'Audiência · off',
      chipClass: 'border-white/20 bg-white/[0.06] text-white/55',
      icon: VideoOff,
      iconClass: 'text-white/45'
    }
  }
  const live = unref(audienceAnalyticsLive)
  if (live == null) {
    return {
      label: 'Audiência · inativo',
      chipClass: 'border-amber-500/35 bg-amber-500/12 text-amber-200',
      icon: Video,
      iconClass: 'text-amber-300/90'
    }
  }
  if (live.capturing && live.diffSamples > 0) {
    return {
      label: 'Audiência · a recolher',
      chipClass: 'border-blue-500/40 bg-blue-500/15 text-blue-200',
      icon: Video,
      iconClass: 'text-blue-300'
    }
  }
  if (live.capturing) {
    return {
      label: 'Audiência · a aquecer',
      chipClass: 'border-cyan-500/35 bg-cyan-500/12 text-cyan-200',
      icon: Video,
      iconClass: 'text-cyan-300 animate-pulse'
    }
  }
  return {
    label: 'Audiência · sem captura',
    chipClass: 'border-amber-500/35 bg-amber-500/12 text-amber-200',
    icon: VideoOff,
    iconClass: 'text-amber-300/90'
  }
})

const audienceAnalyticsCollectingPulse = computed(() => {
  const live = unref(audienceAnalyticsLive)
  return live != null && live.capturing && live.diffSamples > 0
})

const audienceMotionBarPct = computed(() => {
  const s = unref(audienceAnalyticsLive)?.motionScore
  return s != null ? Math.min(100, Math.max(0, Math.round(s * 100))) : 0
})

const audiencePresenceBarPct = computed(() => {
  const s = unref(audienceAnalyticsLive)?.presenceScore
  return s != null ? Math.min(100, Math.max(0, Math.round(s * 100))) : 0
})

const audienceAnalyticsInactiveDetail = computed(() => {
  if (!showAudienceAnalyticsBlock.value || !unref(audienceAnalyticsEnabled) || unref(audienceAnalyticsLive) != null) {
    return ''
  }
  if (unref(lowMemoryMode)) {
    return 'Memória baixa: o player suspende a recolha de audiência.'
  }
  if (unref(capacityTier) === 'low') {
    return 'Tier de hardware baixo: a câmara de audiência fica desligada neste dispositivo.'
  }
  return 'Separador em segundo plano, preview WebRTC do operador, cooldown após preview, ou permissão da câmara recusada.'
})

/**
 * When the tenant has person-CV enabled in admin but the device/policy gates it off, explain why
 * motion/presence may still appear without CV lines on aggregates (mirrors usePlayer personCvEnabled).
 */
const audiencePersonCvDebugHint = computed(() => {
  if (!showAudienceAnalyticsBlock.value || !unref(audienceAnalyticsEnabled)) {
    return ''
  }
  if (unref(tenant)?.audiencePersonCvAnalytics !== true) {
    return ''
  }
  if (unref(lowMemoryMode)) {
    return 'CV (pessoas): desligado — memória baixa.'
  }
  const tier = unref(capacityTier)
  if (tier === 'low') {
    return 'CV (pessoas): desligado — tier de hardware baixo (a recolha de audiência também está off).'
  }
  if (unref(audienceAnalyticsLive)?.personCvModelUnavailable) {
    return 'CV (pessoas): modelo/detector indisponível (TF.js ou COCO-SSD não iniciou). Verifique rede para carregar pesos e a consola do WebView.'
  }
  return ''
})

const weeklyAvailabilityStatus = computed(() => {
  void unref(weeklyAvailabilityGateEpoch)
  void unref(tickEpochMs)
  if (unref(tenant) == null) {
    return null
  }
  return buildPlayerWeeklyAvailabilityStatusDisplay(
    unref(availabilityWeeklySchedule),
    getLocalAsUtcInstant()
  )
})

const weeklyAvailabilityBadge = computed(() => {
  const status = unref(weeklyAvailabilityStatus)
  if (status == null) {
    return null
  }
  if (status.suppressed) {
    return {
      label: 'Pausa semanal',
      chipClass: 'border-violet-500/35 bg-violet-500/15 text-violet-200',
      icon: CalendarClock,
      iconClass: 'text-violet-300'
    }
  }
  return {
    label: 'Janela semanal ativa',
    chipClass: 'border-emerald-500/35 bg-emerald-500/12 text-emerald-200',
    icon: CalendarClock,
    iconClass: 'text-emerald-300'
  }
})

/** Readable APK OTA line (panel mixes PT labels elsewhere). */
function formatApkUpdateBadge(status: string): string {
  switch (status) {
    case 'available':
      return 'atualização disponível'
    case 'downloading':
      return 'a transferir'
    case 'complete':
      return 'instalação iniciada'
    case 'complete_pending_restart':
      return 'transferido · reinicie para instalar'
    case 'failed':
      return 'falhou'
    default:
      return status
  }
}

/** Native app version from getInfo(); used when PackageManager probe is not ready yet. */
const appVersionFromGetInfo = ref<string | null>(null)

/** Native shell `versionName` for the overlay chip (same probe as VBuffer hybrid gate). */
const nativeShellVersionName = ref<string | null>(null)

/** Installed APK/bundle `versionName`; hidden on plain web (no native shell). */
const displayAppVersion = computed(() => {
  if (!isTauriRuntime()) {
    return null
  }
  return overlayInstalledPlayerAppVersion({
    nativeShellVersionName: nativeShellVersionName.value,
    appInfoVersion: appVersionFromGetInfo.value,
    mergedHeartbeatVersion: unref(stateAppVersion),
    preferNativeShell: true
  })
})

/** Android Media3 chip only on Android Tauri — not on browser web. */
const showNativePlaybackSurfaceChip = computed(
  () => isTauriRuntime() && getPlatform() === 'android'
)
/** Same id as x-player-web-version / heartbeat (OTA .version on Tauri when applied). */
const webBundleId = ref<string | null>(null)
const webBundleResolved = ref(false)
const webBundleDisplay = computed(() => {
  if (!webBundleResolved.value) {
    return '…'
  }
  return webBundleId.value != null && webBundleId.value !== '' ? webBundleId.value : '—'
})

const webBundleOutdated = computed(() => {
  const offered = unref(offeredWebOtaVersion)
  if (offered == null || offered === '') {
    return false
  }
  if (!webBundleResolved.value) {
    return false
  }
  const cur = webBundleId.value != null ? normalizeWebOtaVersionToken(webBundleId.value) : ''
  const off = normalizeWebOtaVersionToken(offered)
  if (cur === '') {
    return true
  }
  return cur.toLowerCase() !== off.toLowerCase()
})

const webBundleHighlightClass = computed(() => {
  if (!webBundleResolved.value) {
    return 'text-white/50'
  }
  if (webBundleOutdated.value) {
    return 'font-semibold text-amber-300'
  }
  return 'font-semibold text-white'
})

async function refreshNativePlaybackSurfaceChip(): Promise<void> {
  if (!showNativePlaybackSurfaceChip.value) {
    nativeShellVersionName.value = null
    return
  }
  try {
    nativeShellVersionName.value = await getNativeShellAppVersionName()
  }
  catch {
    nativeShellVersionName.value = null
  }
}

const nativePlaybackSurfaceChip = computed(() =>
  nativePlaybackSurfaceOverlayChip({
    tenantFlag: unref(tenant)?.nativePlaybackSurface,
    shellVersionName: nativeShellVersionName.value
  })
)

watch(
  () => unref(tenant)?.nativePlaybackSurface !== false,
  () => {
    void refreshNativePlaybackSurfaceChip()
  }
)

onMounted(async () => {
  void refreshNativePlaybackSurfaceChip()
  try {
    const info = await getPlatformInstance().app.getInfo()
    if (info?.version) { appVersionFromGetInfo.value = info.version }
  }
  catch {
    // getInfo() is null on web; on Android invoke may throw if Tauri not ready
  }
  try {
    webBundleId.value = await Promise.resolve(getWebBundleVersion())
  }
  catch {
    webBundleId.value = getEmbeddedWebBundleVersion()
  }
  finally {
    webBundleResolved.value = true
  }
})

const containerClasses = computed(() =>
  getCornerPositionClasses(
    unref(currentStateCorner),
    cssEffectiveScreenRotationFromState(state)
  )
)

const roundTripTime = computed(() => {
  const _rtt = unref(rtt)
  return _rtt === null ? 'n/a' : `${_rtt.toString()}ms`
})

const formatNextCampaignStart = computed(() =>
  formatCampaignStartDate(unref(nextCampaignAsLegacy)?.startDate ?? null)
)

const tickEpochMs = ref(getLocalAsUtcInstant().epochMilliseconds)
const tickClock = setInterval(() => {
  tickEpochMs.value = getLocalAsUtcInstant().epochMilliseconds
}, 1000)

/** Local triple-tap auto-hide only; empty when Studio/MCP opened the panel. */
const localAutoHideCountdown = computed(() => {
  void unref(tickEpochMs)
  return formatLocalStatusOverlayAutoHideRemaining(
    getLocalStatusOverlayAutoHideUntilMs(),
    Date.now()
  )
})

/**
 * Soft-defer / registration refresh in flight — discrete spinner on the Feed row
 * (no extra Atualiz. info-row that changes overlay height).
 */
const contentChangeBusy = computed(() => {
  // Depend on tickEpochMs so registration-ahead settles clear within ~1s.
  void unref(tickEpochMs)
  const nowInstant = getLocalAsUtcInstant()
  return resolveContentChangeStatusOverlayLine({
    registrationStatusRefreshPending: registrationStatusRefreshPending.value,
    pendingPlaylistSwap: pendingPlaylistSwap.value,
    registrationAheadOfPlayback: isRegistrationScheduleAheadOfOnAir({
      roots: registrationScheduleRoots.value,
      assignedMasterPlaylistUrn: assignedMasterPlaylistUrn.value,
      campaign: campaign.value,
      now: nowInstant
    }),
    nowMs: Date.now()
  }) != null
})

/** Offline / cached registration snapshot indicator (only while playing from IDB fallback). */
const cachedConfigurationLine = computed((): string | null => {
  if (unref(registrationConfigurationSource) !== 'cached') {
    return null
  }
  // Depend on tickEpochMs so the age label refreshes once per second while the panel is open.
  void unref(tickEpochMs)
  const age = formatRegistrationSnapshotAge(unref(registrationSnapshotSavedAtMs), Date.now())
  return age != null ? `Cache local · há ${age}` : 'Cache local'
})

/** When registered with an empty queue, surface why idle is showing (video skip, aspect, schedule). */
const idleQueueFeedbackLine = computed(() => {
  if (unref(tenant) == null || unref(queue).length >= 1) {
    return null
  }
  void unref(tickEpochMs)
  const t = unref(tenant)!
  const excludeFramesWithVideo
    = unref(capacityTier) === 'low' && t.playerAllowVideoOnLowCapacity !== true
  const restrict = t.playerRestrictPlaybackToDeviceAspectRatio !== false
  const { width, height } = unref(screenSize)
  const { width: ew, height: eh } = effectiveViewportDimensionsForAspect(
    width,
    height,
    cssEffectiveScreenRotationFromState(state)
  )
  const viewportAspect = aspectRatioFromViewportDimensions(ew, eh)
  return resolvePlayerIdleWaitingFeedback({
    campaignLoadError: unref(campaignLoadError),
    campaign: unref(campaign),
    queueLength: unref(queue).length,
    excludeFramesWithVideo,
    allowedAspectRatios: restrict ? new Set([viewportAspect]) : null,
    portalBookViewportAspect: viewportAspect,
    operatorMasterPlaylistUrn: unref(assignedMasterPlaylistUrn),
    referenceInstant: getLocalAsUtcInstant(),
    geoPoint: resolvePlaybackGeoPointForSchedule(
      unref(geolocationPosition),
      unref(campaign)
    )
  })
})

/** Current time (local time treated as UTC) (dd/MM/yyyy HH:mm:ss). */
const currentTime = computed(() => {
  const zoned = Temporal.Instant.fromEpochMilliseconds(unref(tickEpochMs)).toZonedDateTimeISO('UTC')
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(zoned.day)}/${pad(zoned.month)}/${zoned.year} ${pad(zoned.hour)}:${pad(zoned.minute)}:${pad(zoned.second)}`
})

let debugModalPositionInterval: any = null
debugModalPositionInterval = setInterval(() => {
  currentStateCorner.value = unref(currentStateCorner) > 2 ? 0 : unref(currentStateCorner) + 1
}, 60 * 1000)

const idx = ref(getPlatform() === 'web' ? -1 : -1)

const opening = ref(false)
const cacheScanBusy = ref(false)
const {
  promptOpen: kioskPromptOpen,
  pinEntry: kioskPinEntry,
  wrongPin: kioskWrongPin,
  attemptsLeft: kioskAttemptsLeft,
  inCooldown: kioskInCooldown,
  cooldownRemainingSec: kioskCooldownRemainingSec,
  keypadFocusIdx: kioskFocusIdx,
  promptTitle: kioskPromptTitle,
  ensureServiceAccess,
  cancelPrompt: kioskCancelPrompt,
  focusKeypadCell: kioskFocusKeypadCell,
  activateKeypadCell: kioskActivateKeypadCell
} = useKioskServiceUnlock()

/**
 * Android WebView often only delivers DPAD/cursor keydown when a DOM node inside the page is focused.
 * Keep native focus on the virtual keypad cell (or the overlay shell) while the prompt is open.
 */
const kioskOverlayEl = ref<HTMLElement | null>(null)
function syncKioskKeypadDomFocus(cellIdx: number = unref(kioskFocusIdx)): void {
  const cell = document.querySelector<HTMLElement>(`[${KIOSK_KEYPAD_CELL_ATTR}="${cellIdx}"]`)
  if (cell) {
    cell.focus({ preventScroll: true })
    return
  }
  kioskOverlayEl.value?.focus({ preventScroll: true })
}
watch(kioskPromptOpen, (open) => {
  if (open) {
    // Keep virtual focus and DOM focus on cell 0 together (stale idx would Enter the wrong key).
    kioskFocusKeypadCell(0)
    void nextTick(() => syncKioskKeypadDomFocus(0))
  }
})
watch(kioskFocusIdx, (cellIdx) => {
  if (!unref(kioskPromptOpen)) {
    return
  }
  void nextTick(() => syncKioskKeypadDomFocus(cellIdx))
})

function kioskKeypadCellLabel(cell: TKioskKeypadCell): string {
  if (cell.kind === 'digit') {
    return cell.digit
  }
  return cell.kind === 'clear' ? 'Limpar' : 'OK'
}

function kioskKeypadCellDisabled(cell: TKioskKeypadCell): boolean {
  if (cell.kind === 'clear') {
    return false
  }
  if (unref(kioskInCooldown)) {
    return true
  }
  return cell.kind === 'submit' && unref(kioskPinEntry).length < 4
}

/** Keep cells focusable for D-pad (HTML disabled blocks focus); gate activation instead. */
function onKioskKeypadCellClick(cellIdx: number, cell: TKioskKeypadCell): void {
  if (kioskKeypadCellDisabled(cell)) {
    return
  }
  kioskActivateKeypadCell(cellIdx)
}

function kioskKeypadCellClass(cell: TKioskKeypadCell): string {
  if (cell.kind === 'submit') {
    return 'border-emerald-400/35 bg-emerald-500/20 text-[10px] font-semibold text-emerald-200'
  }
  if (cell.kind === 'clear') {
    return 'border-white/15 bg-white/[0.03] text-[10px] font-medium text-white/70'
  }
  return 'border-white/15 bg-white/[0.06]'
}

const { openWifiSetup } = useWifiProvisioning()

const buttons = computed(() => [
  {
    icon: RotateCcw,
    iconClass: '',
    tooltip: 'Rodar ecrã 90° para a esquerda',
    fn: rotateScreenLeft
  },
  {
    icon: RotateCw,
    iconClass: '',
    tooltip: 'Rodar ecrã 90° para a direita',
    fn: rotateScreenRight
  },
  {
    icon: HardDriveDownload,
    iconClass: unref(cacheScanBusy) ? 'animate-pulse text-sky-300' : '',
    tooltip: 'Reparar cache de recursos (MIME, blobs e nova transferência)',
    fn: async () => {
      if (unref(cacheScanBusy)) {
        return
      }
      cacheScanBusy.value = true
      try {
        await scanCachedBinaryAssetsAndReloadPlayback()
      }
      finally {
        cacheScanBusy.value = false
      }
    }
  },
  {
    icon: Wifi,
    iconClass: '',
    if: getPlatform() === 'android',
    tooltip: wifiText('overlay.tooltip'),
    fn: () => {
      void openWifiSetup(unref(tenant)?.fieldServicePin)
    }
  },
  {
    icon: Settings,
    iconClass: unref(opening) ? 'animate-spin' : '',
    if: getPlatform() === 'android',
    tooltip: 'Sair do modo kiosk e abrir definições do sistema',
    fn: async () => {
      // On a kiosk-locked screen this asks for the org service PIN and drops Lock Task first —
      // Android refuses to start Settings while the task is pinned.
      if (!await ensureServiceAccess(unref(tenant)?.fieldServicePin)) {
        return
      }
      opening.value = true
      await getPlatformInstance().openSystemSettings()
      opening.value = false
    }
  }
])

let oldIdx = 0
function keydownEvtListener(evt: KeyboardEvent) {
  // The kiosk PIN keypad's own capture-phase listener already stops propagation, but this early
  // return keeps the toolbar nav inert even if an event somehow still reaches here.
  if (isKioskServicePromptOpen() || isWifiSetupSheetOpen()) {
    return
  }
  const key = androidRemoteKey(evt)
  if (unref(idx) < 0) {
    if (['ArrowUp', 'ArrowDown'].includes(key)) {
      idx.value = oldIdx
    }
    else if (key === 'ArrowLeft') {
      idx.value = unref(buttons).length - 1
    }
    else if (key === 'ArrowRight') {
      idx.value = 0
    }
  }
  else if (key === 'Enter') {
    if (unref(idx) > -1) {
      unref(buttons)[unref(idx)]?.fn()
    }
  }
  else if (unref(idx) > -1) {
    if (['ArrowUp', 'ArrowDown'].includes(key)) {
      oldIdx = unref(idx)
      idx.value = -1
    }
    else if (key === 'ArrowRight') {
      idx.value = (unref(idx) + 1) % buttons.value.length
    }
    else if (key === 'ArrowLeft') {
      idx.value = unref(idx) - 1 < 0 ? unref(buttons).length - 1 : unref(idx) - 1
    }
  }
}

if (getPlatform() === 'android') {
  window.addEventListener('keydown', keydownEvtListener)
}

onBeforeUnmount(() => {
  if (getPlatform() === 'android') {
    window.removeEventListener('keydown', keydownEvtListener)
  }
  kioskCancelPrompt()
  closeWifiSetupSheet()
  clearInterval(tickClock)
  clearInterval(debugModalPositionInterval)
})
</script>

<style scoped>
@reference "../assets/main.css";

.btn-group {
  @apply w-full flex items-center justify-start gap-1 xl:gap-1.5;
}
.btn-with-tip {
  @apply relative flex;
}
.cs-tooltip {
  @apply pointer-events-none absolute bottom-full left-1/2 z-[60] mb-1 max-w-[min(240px,calc(100vw-2rem))] -translate-x-1/2 whitespace-normal rounded-md border border-white/20 bg-zinc-950/[0.92] px-2 py-1 text-center text-[10px] font-medium leading-snug text-white/95 shadow-lg shadow-black/40 opacity-0 backdrop-blur-md transition-opacity duration-150;
  text-shadow: 0 1px 2px rgb(0 0 0 / 0.85);
}
.btn-with-tip:hover .cs-tooltip,
.btn-with-tip:focus-within .cs-tooltip {
  @apply opacity-100;
}
.btn {
  @apply flex size-6 items-center justify-center rounded-md border border-white/15 bg-white/[0.06] p-0.5 text-xs
         transition-colors hover:bg-white/[0.12] data-[active="true"]:bg-white/[0.2] xl:size-8 xl:text-base;
}
.info-row {
  @apply flex min-w-0 items-center gap-1.5;
}
.info-label {
  /* PT labels like "Capacidade" need more than 4.5em at 10px */
  @apply w-[5.75em] shrink-0 font-medium text-white/[0.55] xl:w-[6em];
}
.info-row > :not(.info-label):not(.ml-auto) {
  @apply min-w-0;
}
.audience-meter {
  @apply mt-1;
}
.audience-meter__head {
  @apply mb-0.5 flex items-center justify-between text-[9px] text-white/45;
}
.audience-meter__track {
  @apply h-1 w-full overflow-hidden rounded-full bg-white/10;
}
.audience-meter__fill {
  @apply h-full max-w-full rounded-full transition-[width] duration-300 ease-out;
}
.audience-meter__fill--motion {
  @apply bg-cyan-400/85;
  box-shadow: 0 0 10px rgb(34 211 238 / 0.25);
}
.audience-meter__fill--presence {
  @apply bg-violet-400/85;
  box-shadow: 0 0 10px rgb(167 139 250 / 0.25);
}
</style>
